import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import { WEEKDAYS } from '../../../../consts/date';
import { EventsData } from '../../gameSchedule.types';
import { DayCell } from './DayCell';

interface CalendarProps {
  months: Dayjs[];
  eventsData: EventsData;
  onDateSelect: (date: Dayjs) => void;
  onPrevious: () => void;
  onNext: () => void;
}

export const Calendar: React.FC<CalendarProps> = ({ months, eventsData, onDateSelect, onPrevious, onNext }) => {
  type DayItem = { date: Dayjs; isCurrentMonth: boolean };

  const generateDays = (month: Dayjs): DayItem[] => {
    const startDay = month.startOf('month').day();
    const daysInMonth = month.daysInMonth();
    const adjustedStart = startDay === 0 ? 6 : startDay - 1;
    const days: DayItem[] = [];

    const prevMonth = month.subtract(1, 'month');
    for (let i = adjustedStart - 1; i >= 0; i--) {
      days.push({ date: prevMonth.date(prevMonth.daysInMonth() - i), isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) days.push({ date: month.date(i), isCurrentMonth: true });

    const nextMonth = month.add(1, 'month');
    for (let i = 1; days.length < 42; i++) days.push({ date: nextMonth.date(i), isCurrentMonth: false });

    return days;
  };

  const daysPerMonth = useMemo(() => months.map((m) => generateDays(m)), [months]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(2, 1fr)',
          width: '100%',
        }}
      >
        {months.map((month, index) => {
          const days = daysPerMonth[index];
          const isFirst = index === 0;
          const isLast = index === months.length - 1;

          return (
            <Box
              key={month.format('YYYY-MM')}
              sx={{
                border: '1px solid #EAECF0',
                p: 1,
                bgcolor: '#fff',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {isFirst && (
                <IconButton
                  onClick={onPrevious}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 8,
                    zIndex: 1,
                  }}
                  size="small"
                >
                  <ChevronLeft />
                </IconButton>
              )}

              {isLast && (
                <IconButton
                  onClick={onNext}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 8,
                    zIndex: 1,
                  }}
                  size="small"
                >
                  <ChevronRight />
                </IconButton>
              )}

              <Typography
                textAlign="center"
                marginBottom="8px"
                fontWeight={600}
                fontSize="16px"
                color="#344054"
                lineHeight="24px"
              >
                {month.format('YYYY年M月')}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1, gap: '4px' }}>
                {WEEKDAYS.map((day) => (
                  <Box
                    key={day}
                    sx={{
                      textAlign: 'center',
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#667085',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {day}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', justifyItems: 'center' }}>
                {days.map(({ date, isCurrentMonth }, idx) => (
                  <DayCell
                    key={`${month.format('YYYY-MM')}-${idx}`}
                    date={date}
                    isCurrentMonth={isCurrentMonth}
                    schedule={eventsData[date.format('YYYY-MM-DD')]}
                    onDateSelect={onDateSelect}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
